import React, { useEffect, useRef } from 'react';
import type * as ThreeTypes from 'three';
import { useTheme } from '../theme';

const CANVAS_SIZE = 308;

interface MahoragaPixelWheelProps {
  variant?: 'default' | 'shadow';
  spin?: boolean;
  wrapperClassName?: string;
  canvasClassName?: string;
}

const MahoragaPixelWheel: React.FC<MahoragaPixelWheelProps> = ({
  variant = 'default',
  spin = true,
  wrapperClassName = '',
  canvasClassName = 'block w-[248px] h-[248px] sm:w-[308px] sm:h-[308px]',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let disposed = false;
    let cleanup: (() => void) | undefined;

    const init = async () => {
      const [THREE, { GLTFLoader }] = await Promise.all([
        import('three'),
        import('three/examples/jsm/loaders/GLTFLoader.js'),
      ]);
      if (disposed) return;

      const webglContext = canvas.getContext('webgl2');
      if (!webglContext) return;

      const renderer = new THREE.WebGLRenderer({
        canvas,
        context: webglContext,
        alpha: true,
        antialias: false,
        powerPreference: 'high-performance',
      });
      renderer.setPixelRatio(1);
      renderer.setSize(CANVAS_SIZE, CANVAS_SIZE, false);
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const scene = new THREE.Scene();
      const orthoSize = 1.65;
      const camera = new THREE.OrthographicCamera(-orthoSize, orthoSize, orthoSize, -orthoSize, 0.1, 100);
      camera.position.set(0, 0, 5);
      camera.lookAt(0, 0, 0);

      const modelGroup = new THREE.Group();
      scene.add(modelGroup);
      scene.add(new THREE.AmbientLight(0xffffff, 1));

      let model: ThreeTypes.Object3D | null = null;
      const loader = new GLTFLoader();
      const modelBaseColor = variant === 'shadow' ? '#8f949c' : theme.mood === 'dark' ? '#f8fafc' : theme.text.primary;
      const materialTemplate = new THREE.MeshBasicMaterial({
        color: new THREE.Color(modelBaseColor),
        transparent: variant === 'shadow',
        opacity: variant === 'shadow' ? 0.23 : 1,
      });

      const loadTimer = window.setTimeout(() => {
        loader.load('/3dWheelModel.glb', (gltf) => {
          if (disposed) return;
          model = gltf.scene;
          model.rotation.set(Math.PI / 2, 0, 0);
          model.traverse((obj) => {
            if ((obj as ThreeTypes.Mesh).isMesh) {
              const mesh = obj as ThreeTypes.Mesh;
              mesh.castShadow = false;
              mesh.receiveShadow = false;
              mesh.material = materialTemplate.clone();
            }
          });
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const center = box.getCenter(new THREE.Vector3());
          model.position.sub(center);
          const maxDim = Math.max(size.x, size.y, size.z, 0.001);
          model.scale.setScalar(2.65 / maxDim);
          modelGroup.add(model);
        }, undefined, () => {});
      }, 2200);

      let raf = 0;
      let last = performance.now();
      let rollSpin = 0;
      const animate = (now: number) => {
        const dt = (now - last) / 1000;
        last = now;
        rollSpin = spin ? rollSpin - dt * 0.85 : 0;
        modelGroup.rotation.set(0, 0, rollSpin);
        renderer.render(scene, camera);
        raf = window.requestAnimationFrame(animate);
      };
      raf = window.requestAnimationFrame(animate);

      const resize = () => {
        renderer.setSize(CANVAS_SIZE, CANVAS_SIZE, false);
        camera.left = -orthoSize;
        camera.right = orthoSize;
        camera.top = orthoSize;
        camera.bottom = -orthoSize;
        camera.updateProjectionMatrix();
      };
      resize();

      cleanup = () => {
        window.clearTimeout(loadTimer);
        window.cancelAnimationFrame(raf);
        materialTemplate.dispose();
        if (model) {
          model.traverse((obj) => {
            if ((obj as ThreeTypes.Mesh).isMesh) {
              const mesh = obj as ThreeTypes.Mesh;
              mesh.geometry?.dispose();
              const mat = mesh.material;
              if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
              else mat?.dispose();
            }
          });
        }
        renderer.dispose();
      };
    };

    const initTimer = window.setTimeout(init, 800);

    return () => {
      disposed = true;
      window.clearTimeout(initTimer);
      cleanup?.();
    };
  }, [theme.mood, theme.text.primary, spin, variant]);

  return (
    <div
      className={`relative shrink-0 overflow-hidden ${wrapperClassName}`}
      style={{ backgroundColor: 'transparent', boxShadow: 'none' }}
      aria-hidden="true"
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className={canvasClassName}
        style={{ imageRendering: 'pixelated' }}
      />
    </div>
  );
};

export default MahoragaPixelWheel;
